import React, { useState, useEffect } from 'react';
import { useForm, zodResolver } from '@mantine/form';
import { Box, Button, Flex, Group, Notification, Paper, Select, Stack, TextInput, Title } from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import * as z from 'zod';
import i18n, { t } from 'i18next';
import { IconArrowLeft, IconAt, IconWorld, IconHospital, IconCalendar } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { usePostPatientInvitations } from '../../../global/entities/PatientInvitations/mutations/usePostPatientInvitationsMutations.tsx';
import { IPatientInvitations } from '../../../global/entities/PatientInvitations/PatientInvitations.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { useGetPatients } from '../../../global/entities/Patients/queries/useGetPatients.tsx';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../global/style/theme.tsx';
import { useGetTherapeuticIndicationsForHcp } from '../../../global/entities/healthcareFacility/queries/useGetTherapeuticIndicationsForHcp.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { useGetHealthcareFacilitiesForHcp } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilitiesForHcp.tsx';
import { PostHogService } from '../../../global/services/posthogService.ts';
import { useGetCarePlanTemplatesForInvitation } from '../../../global/entities/CarePlanTemplate/queries/useGetCarePlanTemplatesForInvitation.tsx';
import Utils from '../../../global/helpers/utils.ts';

interface IHealthcareFacilityForHcp {
    id: number;
    name: string;
    isAnonymizedMode: boolean;
}

const PatientInvitationsForm: React.FC = inject()(
    observer(() => {
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const postPatientInvitations = usePostPatientInvitations();
        const { refetch: refetchPatients } = useGetPatients();
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();
        const { data: therapeuticIndications } = useGetTherapeuticIndicationsForHcp(
            currentUser?.healthcareProfessionalID ? parseInt(currentUser.healthcareProfessionalID) : undefined
        );
        const { data: healthcareFacilities } = useGetHealthcareFacilitiesForHcp(
            currentUser?.healthcareProfessionalID ? parseInt(currentUser.healthcareProfessionalID) : undefined
        );

        const schema = z.object({
            firstName: z.string().min(1, { message: t('formError.firstNameIsRequired') }),
            lastName: z.string().min(1, { message: t('formError.lastNameIsRequired') }),
            email: z.string().email({ message: t('formError.invalidEmailAddress') }),
            language: z.string().nullable().refine(val => val !== null && val.length > 0, { message: t('formError.languageIsRequired') }),
            therapeuticIndicationId: z.string().nullable().refine(val => val !== null && val.length > 0, { message: t('formError.therapeuticIndicationIsRequired') }),
            healthcareFacilityId: z.string().nullable().refine(val => val !== null && val.length > 0, { message: t('formError.facilityRequired') }),
            fileNumber: z.string().max(50).optional().or(z.literal('')),
            carePlanTemplateId: z.string().nullable(),
            eventDate: z.date().nullable(),
        });

        const form = useForm({
            initialValues: {
                firstName: '',
                lastName: '',
                email: '',
                language: null as string | null,
                therapeuticIndicationId: null as string | null,
                healthcareFacilityId: null as string | null,
                fileNumber: '',
                carePlanTemplateId: null as string | null,
                eventDate: null as Date | null,
            },
            validate: zodResolver(schema),
        });

        // Check if selected facility is in anonymized mode
        const selectedFacility = (healthcareFacilities as IHealthcareFacilityForHcp[] | undefined)?.find(
            facility => facility.id.toString() === form.values.healthcareFacilityId
        );
        const isAnonymizedMode = selectedFacility?.isAnonymizedMode || false;

        const facilityIdForPlans = form.values.healthcareFacilityId
            ? parseInt(form.values.healthcareFacilityId, 10)
            : undefined;
        const therapeuticIndicationIdForPlans = form.values.therapeuticIndicationId
            ? parseInt(form.values.therapeuticIndicationId, 10)
            : undefined;
        const { data: carePlanTemplates } = useGetCarePlanTemplatesForInvitation(
            facilityIdForPlans,
            therapeuticIndicationIdForPlans
        );

        const selectedCarePlan = carePlanTemplates?.find(
            plan => plan.id.toString() === form.values.carePlanTemplateId
        );

        // Clear care plan + event date when facility or therapeutic indication change
        useEffect(() => {
            form.setFieldValue('carePlanTemplateId', null);
            form.setFieldValue('eventDate', null);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [form.values.healthcareFacilityId, form.values.therapeuticIndicationId]);

        // Clear event date when the selected care plan changes
        useEffect(() => {
            form.setFieldValue('eventDate', null);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [form.values.carePlanTemplateId]);

        // Pre-select the facility from the HCP's current session
        useEffect(() => {
            if (currentUser?.selectedHealthcareFacilityID)
                form.setFieldValue('healthcareFacilityId', currentUser.selectedHealthcareFacilityID.toString());

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentUser?.selectedHealthcareFacilityID]);

        const handleCancel = () => {
            form.reset();
            navigate('/hcpDashboard/patient');
        };

        const handleSubmit = async (values: typeof form.values) => {
            setError(null);
            setIsSubmitting(true);
            try {
                const payload: IPatientInvitations = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    language: values.language!,
                    therapeuticIndicationId: parseInt(values.therapeuticIndicationId!, 10),
                    healthcareFacilityId: parseInt(values.healthcareFacilityId!, 10),
                    fileNumber: values.fileNumber || undefined,
                    carePlanTemplateId: values.carePlanTemplateId
                        ? parseInt(values.carePlanTemplateId, 10)
                        : undefined,
                    eventDate: values.eventDate
                        ? Utils.formatLocalDateToYYYYMMDD(values.eventDate)
                        : undefined,
                };
                await postPatientInvitations.mutateAsync(payload);
                PostHogService.capture('patient invitation sent', {
                    therapeutic_indication_id: payload.therapeuticIndicationId,
                    language: payload.language,
                    is_anonymized_mode: isAnonymizedMode,
                });
                await refetchPatients();
                handleCancel();
            } catch (e) {
                if (e instanceof AppMedError)
                    form.setFieldError('email', e.message);
                else
                    setError('An unexpected error occurred.');
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <Stack gap={'lg'} pt={'xl'}>
                <Flex align={'center'} justify={'space-between'}>
                    <Button
                        leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
                        onClick={() => navigate('/hcpDashboard/patient')}
                        variant="transparent"
                    >
                        <Title c="black" order={3}>
                            {t('page.patient.invitePatient')}
                        </Title>
                    </Button>
                </Flex>
                <Paper>
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <Stack>
                            <Group grow>
                                <TextInput
                                    label={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantNumber') || 'Participant Number'
                                            : t('formGlobal.firstName')
                                    }
                                    placeholder={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantNumberPlaceholder') || 'e.g., P-0123'
                                            : t('formGlobal.enterFirstName')
                                    }
                                    description={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantNumberDescription') ||
                                              'Enter the participant number (e.g., P-0123)'
                                            : undefined
                                    }
                                    {...form.getInputProps('firstName')}
                                />
                                <TextInput
                                    label={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantInitial') || 'Participant Initial'
                                            : t('formGlobal.lastName')
                                    }
                                    placeholder={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantInitialPlaceholder') || 'e.g., AB'
                                            : t('formGlobal.enterLastName')
                                    }
                                    description={
                                        isAnonymizedMode
                                            ? t('formInvitation.participantInitialDescription') ||
                                              'Enter the participant initial (e.g., AB)'
                                            : undefined
                                    }
                                    {...form.getInputProps('lastName')}
                                />
                            </Group>
                            <TextInput
                                label={t('formGlobal.fileNumber')}
                                placeholder={t('formGlobal.enterFileNumber')}
                                maxLength={50}
                                {...form.getInputProps('fileNumber')}
                            />
                            <TextInput
                                label={t('formGlobal.email')}
                                placeholder={t('formGlobal.enterEmailAddress')}
                                leftSection={<IconAt size={18} />}
                                {...form.getInputProps('email')}
                            />
                            <Select
                                label={t('hcf.title')}
                                placeholder={t('hcf.select')}
                                description={t('hcf.facilityLockedDescription')}
                                leftSection={<IconHospital size={18} />}
                                disabled
                                data={healthcareFacilities?.map(facility => ({
                                    value: facility.id.toString(),
                                    label: facility.name
                                })) || []}
                                {...form.getInputProps('healthcareFacilityId')}
                            />
                            <Flex gap="md">
                                <Box flex="1">
                                    <Select
                                        label={t('formGlobal.language')}
                                        placeholder={t('formGlobal.selectLanguage')}
                                        leftSection={<IconWorld size={18} />}
                                        data={[
                                            { value: 'en', label: t('select.language.en') },
                                            { value: 'fr', label: t('select.language.fr') },
                                        ]}
                                        {...form.getInputProps('language')}
                                    />
                                </Box>
                                <Box flex="3">
                                    <Select
                                        label={t('formGlobal.therapeuticIndication')}
                                        placeholder={t('formGlobal.selectTherapeuticIndication')}
                                        data={therapeuticIndications?.map(indication => ({
                                            value: indication.id.toString(),
                                            label: indication.name
                                        })) || []}
                                        {...form.getInputProps('therapeuticIndicationId')}
                                    />
                                </Box>
                            </Flex>
                            {carePlanTemplates && carePlanTemplates.length > 0 && (
                                <Select
                                    label={t('carePlan.invitationLabel')}
                                    placeholder={t('carePlan.invitationPlaceholder')}
                                    clearable
                                    data={[
                                        { value: '', label: t('carePlan.none') },
                                        ...carePlanTemplates.map(plan => ({
                                            value: plan.id.toString(),
                                            label: i18n.language === 'fr' ? plan.nameFr : plan.nameEn,
                                        })),
                                    ]}
                                    value={form.values.carePlanTemplateId ?? ''}
                                    onChange={value =>
                                        form.setFieldValue('carePlanTemplateId', value || null)
                                    }
                                />
                            )}
                            {selectedCarePlan && (
                                <CalendarDateInput
                                    label={
                                        (i18n.language === 'fr'
                                            ? selectedCarePlan.eventLabelFr
                                            : selectedCarePlan.eventLabelEn) ||
                                        t('carePlan.eventDate')
                                    }
                                    placeholder={t('placeHolder.dateFormat')}
                                    leftSection={<IconCalendar size={16} />}
                                    clearable
                                    description={
                                        selectedCarePlan.eventDateRequired
                                            ? t('carePlan.eventDateHint')
                                            : undefined
                                    }
                                    {...form.getInputProps('eventDate')}
                                />
                            )}
                            <Group mt="md">
                                <Button ml={'auto'} variant="default" onClick={handleCancel}>
                                    {t('button.cancel')}
                                </Button>
                                <Button type="submit" loading={isSubmitting}>
                                    {t('formInvitation.sendInvite')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                    {error && (
                        <Notification color="red" onClose={() => setError(null)}>
                            {error}
                        </Notification>
                    )}
                </Paper>
            </Stack>
        );
    }),
);
export default PatientInvitationsForm;
